'use client';

import { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion, useInView } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Globe, LayoutTemplate, Bot, DatabaseZap, FileText, Check, FileCheck, Upload, MessageCircle, Mail, Smartphone, ArrowRight, Layers } from 'lucide-react';
import Link from 'next/link';

gsap.registerPlugin(ScrollTrigger);

const easing = [0.22, 1, 0.36, 1] as const;

const solutions = [
  {
    number: '01',
    icon: Globe,
    tag: 'Siti Web',
    title: 'Bello da vedere.\nPotente da dentro.',
    description:
      'Siti web rapidi, rifiniti e pixel perfect su ogni dispositivo. Core Web Vitals al massimo, UX progettata per convertire. Dal brief al go-live in settimane.',
    points: ['Lighthouse 100 su ogni pagina, ogni device', 'Responsive dal 375px al 4K, senza compromessi', 'SEO on-page, accessibilità e conversioni integrate'],
    stats: [{ l: 'Lighthouse', v: '100' }, { l: 'CLS', v: '0' }, { l: 'LCP', v: '< 1s' }],
    cta: 'Parliamo del progetto',
    bg: '#010408',
    accent: 'rgba(59,130,246,1)',
    accentMid: 'rgba(59,130,246,0.12)',
    bars: [20, 38, 28, 55, 42, 70, 56, 84, 65, 90, 76, 100],
  },
  {
    number: '02',
    icon: LayoutTemplate,
    tag: 'Piattaforme Web',
    title: 'Un\'unica piattaforma.\nTutto connesso.',
    description:
      'Un\'esperienza senza interruzioni che unisce ogni parte del tuo lavoro. ERP, CRM, API esterne in un posto solo. Zero silos, zero frustrazioni.',
    points: ['Interfacce React / Next.js incredibilmente veloci', 'Connessa nativamente con ogni sistema che già usi', 'Scalabile quanto la tua ambizione'],
    stats: [{ l: 'Efficienza', v: '+80%' }, { l: 'Tempo', v: '-3x' }, { l: 'Errori', v: '0' }],
    cta: 'Preventivo immediato',
    bg: '#000000',
    accent: 'rgba(230,43,54,1)',
    accentMid: 'rgba(230,43,54,0.12)',
    bars: [35, 60, 42, 78, 52, 88, 65, 95, 58, 82, 72, 100],
  },
  {
    number: '03',
    icon: Bot,
    tag: 'Preventivatori AI',
    title: 'Preventivi perfetti.\nIn secondi, non ore.',
    description:
      'L\'intelligenza artificiale legge, calcola e imposta i margini. Tu premi invio. Un vantaggio che i tuoi concorrenti ancora non hanno.',
    points: ['Configuratori intelligenti in pochi click', 'Automazioni AI con cataloghi prodotto integrati', 'Più conversioni, meno fatica. Sempre.'],
    stats: [] as { l: string; v: string }[],
    cta: 'Prova il preventivatore',
    bg: '#050508',
    accent: 'rgba(139,92,246,1)',
    accentMid: 'rgba(139,92,246,0.12)',
    bars: [45, 70, 55, 85, 65, 92, 75, 100, 68, 88, 79, 95],
  },
  {
    number: '04',
    icon: DatabaseZap,
    tag: 'CRM Automatizzato',
    title: 'Il CRM che ricorda\ntutto per te.',
    description:
      'Un CRM costruito attorno a come lavori davvero. Lead, opportunità, follow-up e comunicazioni si muovono da soli, senza toccare nulla.',
    points: ['Su misura per il tuo modo di vendere', 'Automazioni che non si dimenticano nulla', 'Visibilità totale. Lavoro manuale zero.'],
    stats: [{ l: 'Lead', v: '+60%' }, { l: 'Mancati', v: '0' }, { l: 'Velocità', v: '+2x' }],
    cta: 'Preventivo gratuito',
    bg: '#020505',
    accent: 'rgba(16,185,129,1)',
    accentMid: 'rgba(16,185,129,0.12)',
    bars: [55, 72, 60, 88, 70, 95, 80, 100, 72, 90, 82, 98],
  },
  {
    number: '05',
    icon: Layers,
    tag: 'Consulenza Digitale',
    title: 'Automatizza\nl\'impensabile.',
    description:
      'Ogni processo nasconde ore sprecate. Le troviamo, le mappiamo, le eliminiamo. In 48 ore sai esattamente dove stai perdendo tempo e denaro.',
    points: [
      'Audit completo dei processi aziendali in 48 ore',
      'Automazioni su misura che eliminano il lavoro ripetitivo',
      'ROI misurabile e garantito dal primo mese',
    ],
    stats: [] as { l: string; v: string }[],
    cta: 'Diagnosi gratuita',
    bg: '#030301',
    accent: 'rgba(245,158,11,1)',
    accentMid: 'rgba(245,158,11,0.10)',
    bars: [] as number[],
  },
];

// ── Mini bar chart ────────────────────────────────────────────────────────────

function BarChart({ bars, accent }: { bars: number[]; accent: string }) {
  return (
    <div className="flex items-end gap-0.5 h-16 w-full">
      {bars.map((h, i) => (
        <motion.div
          key={i}
          className="flex-1 rounded-sm"
          style={{
            height: `${h}%`,
            background: accent.replace('1)', `${0.15 + (i / 12) * 0.75})`),
            transformOrigin: 'bottom',
          }}
          initial={{ scaleY: 0 }}
          whileInView={{ scaleY: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ delay: i * 0.025, duration: 0.45, ease: easing }}
        />
      ))}
    </div>
  );
}

// ── Quote process — 4 step sub-components ────────────────────────────────────

function StepUpload({ accent, year }: { accent: string; year: number }) {
  return (
    <div className="space-y-4 rounded-xl border-2 border-dashed border-white/[0.08] p-5 text-center">
      <div
        className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl border"
        style={{ background: `${accent}12`, borderColor: `${accent}30` }}
      >
        <Upload className="h-5 w-5" style={{ color: accent }} />
      </div>
      <p className="text-[11px] text-white/35">Carica il preventivo fornitore</p>
      <div
        className="inline-flex items-center gap-2 rounded-lg border px-3 py-2"
        style={{ borderColor: 'rgba(239,68,68,0.25)', background: 'rgba(239,68,68,0.08)' }}
      >
        <FileText className="h-3.5 w-3.5 shrink-0 text-red-400" />
        <span className="text-[10px] font-medium text-white/70">
          preventivo_fornitore_{year}.pdf
        </span>
      </div>
      <div className="h-1 overflow-hidden rounded-full bg-white/[0.06]">
        <motion.div
          className="h-full rounded-full"
          style={{ background: accent }}
          initial={{ width: '0%' }}
          animate={{ width: '100%' }}
          transition={{ duration: 1.1, ease: 'easeOut' }}
        />
      </div>
      <p className="text-[9px] text-white/25">Caricamento completato</p>
    </div>
  );
}

function StepAI({ accent }: { accent: string }) {
  const items = [
    { label: 'Righe prodotto lette', done: true },
    { label: 'Margini commerciali applicati', done: true },
    { label: 'Prezzi netti calcolati', done: false },
  ];
  return (
    <div className="space-y-3">
      <div
        className="flex items-center gap-2.5 rounded-xl border px-3.5 py-3"
        style={{ borderColor: `${accent}25`, background: `${accent}09` }}
      >
        <motion.div
          className="h-2 w-2 shrink-0 rounded-full"
          style={{ background: accent }}
          animate={{ opacity: [1, 0.2, 1] }}
          transition={{ duration: 1.1, repeat: Infinity, ease: 'easeInOut' }}
        />
        <span className="text-[11px] font-semibold" style={{ color: accent }}>
          IA in elaborazione…
        </span>
      </div>
      <div className="space-y-2.5 rounded-xl border border-white/[0.05] bg-white/[0.03] p-3.5">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2.5">
            <div
              className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full border"
              style={
                item.done
                  ? { background: 'rgba(16,185,129,0.12)', borderColor: 'rgba(16,185,129,0.3)' }
                  : { background: `${accent}10`, borderColor: `${accent}30` }
              }
            >
              {item.done ? (
                <Check className="h-2.5 w-2.5 text-emerald-400" />
              ) : (
                <motion.div
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ background: accent }}
                  animate={{ opacity: [1, 0.15, 1] }}
                  transition={{ duration: 0.8, repeat: Infinity }}
                />
              )}
            </div>
            <span
              className="text-[10px]"
              style={{ color: item.done ? 'rgba(255,255,255,0.45)' : accent }}
            >
              {item.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepQuote({ accent, year }: { accent: string; year: number }) {
  const date = new Date().toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  return (
    <div
      className="rounded-xl border p-4 space-y-3"
      style={{ borderColor: `${accent}30`, background: `${accent}09` }}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="mb-1 text-[9px] uppercase tracking-[0.12em] text-white/25">Preventivo generato</div>
          <div className="text-[13px] font-semibold text-white">Acme Industries S.r.l.</div>
          <div className="mt-0.5 text-[10px] text-white/30">PRV-{year}-089 · {date}</div>
        </div>
        <div
          className="flex h-7 w-7 items-center justify-center rounded-lg border"
          style={{ background: `${accent}15`, borderColor: `${accent}25` }}
        >
          <FileCheck className="h-3.5 w-3.5" style={{ color: accent }} />
        </div>
      </div>
      <div className="flex items-center justify-between border-t border-white/[0.05] pt-3">
        <span className="text-[9px] text-white/25">847 articoli · pronto all&apos;invio</span>
        <span className="text-base font-bold" style={{ color: accent }}>€ 24.680</span>
      </div>
    </div>
  );
}

function StepSend({ accent }: { accent: string }) {
  const channels = [
    { Icon: MessageCircle, label: 'WhatsApp', color: '#25D366', bg: 'rgba(37,211,102,0.08)', border: 'rgba(37,211,102,0.22)' },
    { Icon: Mail,          label: 'Email',    color: '#60a5fa', bg: 'rgba(96,165,250,0.08)', border: 'rgba(96,165,250,0.22)' },
    { Icon: Smartphone,    label: 'SMS',      color: 'rgba(255,255,255,0.45)', bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.1)' },
  ];
  return (
    <div className="space-y-4">
      <p className="text-center text-[10px] text-white/30">Invia il preventivo tramite</p>
      <div className="grid grid-cols-3 gap-2.5">
        {channels.map(({ Icon, label, color, bg, border }) => (
          <motion.div
            key={label}
            className="flex cursor-pointer flex-col items-center gap-2.5 rounded-xl border p-3.5"
            style={{ background: bg, borderColor: border }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.14 }}
          >
            <Icon className="h-5 w-5" style={{ color }} />
            <span className="text-[9px] font-medium" style={{ color }}>{label}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ── Step display — direction-aware transitions ────────────────────────────────

const STEP_LABELS = ['Carica', 'IA elabora', 'Preventivo', 'Invia'];

function StepDisplay({
  step,
  accent,
  year,
  direction,
}: {
  step: number;
  accent: string;
  year: number;
  direction: 1 | -1;
}) {
  return (
    <>
      {/* Progress rail */}
      <div className="mb-5 flex gap-1.5">
        {STEP_LABELS.map((label, i) => (
          <div key={i} className="flex flex-1 flex-col gap-1">
            <div
              className="h-0.5 rounded-full transition-all duration-300"
              style={{ background: i <= step ? accent : 'rgba(255,255,255,0.08)' }}
            />
            <span
              className="text-[8px] font-medium transition-colors duration-300"
              style={{ color: i === step ? accent : 'rgba(255,255,255,0.2)' }}
            >
              {label}
            </span>
          </div>
        ))}
      </div>

      {/* Step content — pops exiting element out of layout so enter/exit overlap cleanly */}
      <AnimatePresence mode="popLayout">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: direction * 18 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: direction * -12 }}
          transition={{ duration: 0.18, ease: easing }}
        >
          {step === 0 && <StepUpload accent={accent} year={year} />}
          {step === 1 && <StepAI accent={accent} />}
          {step === 2 && <StepQuote accent={accent} year={year} />}
          {step === 3 && <StepSend accent={accent} />}
        </motion.div>
      </AnimatePresence>
    </>
  );
}

// ── Quote process mockup ──────────────────────────────────────────────────────
// Desktop: `step` prop driven by parent GSAP pin scroll progress.
// Mobile: auto-cycles when the card is in view — no extra wrapper height needed.

function QuoteProcessMockup({ accent, step }: { accent: string; step: number }) {
  const mobileRef = useRef<HTMLDivElement>(null);
  const [mobileStep, setMobileStep] = useState(0);
  const year = new Date().getFullYear();

  const prevStepRef = useRef(step);
  const direction: 1 | -1 = step >= prevStepRef.current ? 1 : -1;
  useEffect(() => { prevStepRef.current = step; }, [step]);

  // useInView is safe on desktop too — the effect bails out via matchMedia
  const isInView = useInView(mobileRef, { margin: '-15% 0px' });

  useEffect(() => {
    const mm = gsap.matchMedia();
    mm.add('(max-width: 1023px)', () => {
      if (!isInView) { setMobileStep(0); return; }

      let current = 0;
      let timer: ReturnType<typeof setTimeout>;
      const advance = () => {
        current = (current + 1) % 4;
        setMobileStep(current);
        timer = setTimeout(advance, 2000);
      };
      timer = setTimeout(advance, 1800);
      return () => clearTimeout(timer);
    });
    return () => mm.revert();
  }, [isInView]);

  return (
    <div ref={mobileRef}>
      {/* Desktop: scroll-driven via parent GSAP pin */}
      <div className="hidden lg:block">
        <StepDisplay step={step} accent={accent} year={year} direction={direction} />
      </div>

      {/* Mobile: auto-cycle, no tall wrapper */}
      <div className="lg:hidden">
        <StepDisplay step={mobileStep} accent={accent} year={year} direction={1} />
      </div>
    </div>
  );
}

// ── Website results infographic ───────────────────────────────────────────────
// "Questo sito genera risultati." — Linear / Vercel aesthetic.
// KPIs dominate; SVG flow beneath shows SEO + Performance + UX + Analytics → node → Crescita.

function WebsiteMockup({ accent }: { accent: string }) {
  const nodeX = 146;
  const nodeY = 52;
  const inputs = [
    { label: 'SEO',         y: 12 },
    { label: 'Performance', y: 32 },
    { label: 'UX',          y: 52 },
    { label: 'Analytics',   y: 72 },
  ];

  return (
    <div className="space-y-5">

      {/* ── KPIs — dominant ────────────────────────────── */}
      <div className="grid grid-cols-3 divide-x divide-white/[0.06]">
        {[
          { value: '100',  sub: 'Score SEO' },
          { value: '<1s',  sub: 'LCP' },
          { value: '+37%', sub: 'Lead' },
        ].map((kpi, i) => (
          <motion.div
            key={kpi.sub}
            className="flex flex-col items-center py-3"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ delay: i * 0.09, duration: 0.45, ease: easing }}
          >
            <span
              className="text-5xl font-black leading-none tabular-nums"
              style={{ color: accent }}
            >
              {kpi.value}
            </span>
            <span className="mt-2 text-[10px] uppercase tracking-[0.16em] text-white/30">
              {kpi.sub}
            </span>
          </motion.div>
        ))}
      </div>

      {/* Thin rule */}
      <div className="h-px bg-white/[0.05]" />

      {/* ── Convergence flow ───────────────────────────── */}
      <div className="relative" style={{ height: 100 }}>

        {/* Input labels — top values mirror SVG y-coords (viewBox height = CSS height) */}
        {inputs.map((inp, i) => (
          <motion.div
            key={inp.label}
            className="absolute left-0 leading-none"
            style={{ top: inp.y, transform: 'translateY(-50%)' }}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 + i * 0.06, duration: 0.35, ease: easing }}
          >
            <span className="text-[11px] font-medium tracking-wide text-white/30">
              {inp.label}
            </span>
          </motion.div>
        ))}

        {/* Output label */}
        <motion.div
          className="absolute right-0 leading-none"
          style={{ top: nodeY, transform: 'translateY(-50%)' }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 1.3, duration: 0.35, ease: easing }}
        >
          <span className="text-[11px] font-bold tracking-wide" style={{ color: accent }}>
            ↑ Crescita
          </span>
        </motion.div>

        {/* SVG: paths + node — geometry only */}
        <svg
          className="absolute inset-0 w-full h-full"
          viewBox="0 0 280 100"
          preserveAspectRatio="none"
        >
          {/* Convergence paths */}
          {inputs.map((inp, i) => (
            <motion.path
              key={inp.label}
              d={`M 68 ${inp.y} C 106 ${inp.y} 128 ${nodeY} ${nodeX} ${nodeY}`}
              stroke={accent}
              strokeWidth={1.0}
              strokeOpacity={0.22}
              fill="none"
              initial={{ pathLength: 0, opacity: 0 }}
              whileInView={{ pathLength: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.28 + i * 0.1, duration: 0.55, ease: easing }}
            />
          ))}

          {/* Node — outer glow */}
          <motion.circle
            cx={nodeX} cy={nodeY} r={26}
            fill={accent.replace('1)', '0.045)')}
            style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
            initial={{ scale: 0.2, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.72, duration: 0.55 }}
          />
          {/* Node — mid glow */}
          <motion.circle
            cx={nodeX} cy={nodeY} r={14}
            fill={accent.replace('1)', '0.11)')}
            style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
            initial={{ scale: 0.2, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.75, duration: 0.55 }}
          />
          {/* Node — core */}
          <motion.circle
            cx={nodeX} cy={nodeY} r={4.5}
            fill={accent}
            style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
            initial={{ scale: 0, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.82, duration: 0.28, ease: easing }}
          />

          {/* Output line */}
          <motion.path
            d={`M ${nodeX} ${nodeY} L 226 ${nodeY}`}
            stroke={accent}
            strokeWidth={1.0}
            strokeOpacity={0.38}
            fill="none"
            initial={{ pathLength: 0, opacity: 0 }}
            whileInView={{ pathLength: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.92, duration: 0.42, ease: easing }}
          />
          {/* Output dot */}
          <motion.circle
            cx={226} cy={nodeY} r={2.5}
            fill={accent}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 1.28, duration: 0.2 }}
          />
        </svg>
      </div>
    </div>
  );
}

// ── Platform hub-and-spoke infographic ───────────────────────────────────────
// "Tutti i miei strumenti lavorano insieme."

function PlatformMockup({ accent }: { accent: string }) {
  const hub = { x: 160, y: 80 };
  const lx = 62;
  const rx = 258;
  const ys = [36, 80, 124];
  const leftNodes  = ['ERP', 'CRM', 'E-commerce'];
  const rightNodes = ['Marketing', 'WhatsApp', 'Email'];
  const allSpokes  = [
    ...ys.map((y, i) => ({ x: lx, y, i })),
    ...ys.map((y, i) => ({ x: rx, y, i: i + 3 })),
  ];

  return (
    <svg
      width="100%"
      height="220"
      viewBox="0 0 320 160"
      style={{ display: 'block', overflow: 'visible' }}
    >
      {/* Orbit ring — subtle dashed circle */}
      <motion.circle
        cx={hub.x} cy={hub.y} r={64}
        fill="none"
        stroke="rgba(255,255,255,0.045)"
        strokeWidth={0.5}
        strokeDasharray="3 10"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.25, duration: 0.8 }}
      />

      {/* Spoke lines — hub outward, staggered */}
      {allSpokes.map(({ x, y, i }) => (
        <motion.path
          key={`spoke-${x}-${y}`}
          d={`M ${hub.x} ${hub.y} L ${x} ${y}`}
          stroke={accent}
          strokeWidth={0.65}
          strokeOpacity={0.22}
          fill="none"
          initial={{ pathLength: 0, opacity: 0 }}
          whileInView={{ pathLength: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.48 + i * 0.08, duration: 0.38, ease: easing }}
        />
      ))}

      {/* Hub */}
      <motion.g
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.05, duration: 0.5 }}
      >
        {/* Outer pulse ring */}
        <motion.circle
          cx={hub.x} cy={hub.y} r={30}
          fill={accent.replace('1)', '0.048)')}
          style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
          animate={{ scale: [1, 1.26, 1], opacity: [1, 0.3, 1] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', delay: 0.95 }}
        />
        {/* Mid glow */}
        <circle cx={hub.x} cy={hub.y} r={20} fill={accent.replace('1)', '0.10)')} />
        {/* Inner glow */}
        <circle cx={hub.x} cy={hub.y} r={12} fill={accent.replace('1)', '0.19)')} />
        {/* Core */}
        <circle cx={hub.x} cy={hub.y} r={4.5} fill={accent} />
        {/* Label */}
        <text
          x={hub.x} y={hub.y + 27}
          textAnchor="middle"
          fontSize={6}
          fill="rgba(255,255,255,0.38)"
          fontFamily="system-ui,-apple-system,BlinkMacSystemFont,sans-serif"
          fontWeight="600"
          letterSpacing={2.2}
        >
          PLATFORM
        </text>
      </motion.g>

      {/* Left nodes */}
      {leftNodes.map((label, i) => (
        <motion.g
          key={label}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 + i * 0.1, duration: 0.3 }}
        >
          <circle cx={lx} cy={ys[i]} r={7}   fill={accent.replace('1)', '0.08)')} />
          <circle cx={lx} cy={ys[i]} r={2.5}  fill={accent.replace('1)', '0.65)')} />
          <text
            x={lx - 13} y={ys[i]}
            textAnchor="end" dominantBaseline="middle"
            fontSize={7} fill="rgba(255,255,255,0.38)"
            fontFamily="system-ui,-apple-system,BlinkMacSystemFont,sans-serif"
            letterSpacing={0.3}
          >
            {label}
          </text>
        </motion.g>
      ))}

      {/* Right nodes */}
      {rightNodes.map((label, i) => (
        <motion.g
          key={label}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.64 + i * 0.1, duration: 0.3 }}
        >
          <circle cx={rx} cy={ys[i]} r={7}   fill={accent.replace('1)', '0.08)')} />
          <circle cx={rx} cy={ys[i]} r={2.5}  fill={accent.replace('1)', '0.65)')} />
          <text
            x={rx + 13} y={ys[i]}
            textAnchor="start" dominantBaseline="middle"
            fontSize={7} fill="rgba(255,255,255,0.38)"
            fontFamily="system-ui,-apple-system,BlinkMacSystemFont,sans-serif"
            letterSpacing={0.3}
          >
            {label}
          </text>
        </motion.g>
      ))}
    </svg>
  );
}

// ── CRM Automatizzato timeline mockup ────────────────────────────────────────

function CRMTimelineMockup({ accent, step }: { accent: string; step?: number }) {
  const activeStep = step ?? 0;

  const rows = [
    { label: 'Lead acquisito',         time: '09:14', tag: null,    final: false },
    { label: 'Preventivo inviato',     time: '09:17', tag: 'Auto',  final: false },
    { label: 'Follow-up programmato',  time: '11:30', tag: 'Auto',  final: false },
    { label: 'Telefonata effettuata',  time: '14:22', tag: 'Auto',  final: false },
    { label: 'Offerta aggiornata',     time: '16:45', tag: 'Auto',  final: false },
    { label: 'Contratto firmato',      time: '17:02', tag: 'check', final: true  },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between pb-2">
        <span className="text-[10px] uppercase tracking-[0.18em] text-white/25">
          Pipeline automatizzata
        </span>
        <div className="flex items-center gap-2">
          <motion.div
            className="h-2 w-2 rounded-full"
            style={{ background: accent }}
            animate={{ opacity: [1, 0.25, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          />
          <span className="text-[10px] font-medium" style={{ color: accent }}>
            In esecuzione
          </span>
        </div>
      </div>

      <div className="relative">
        {/* Guide rail always visible */}
        <div className="absolute left-[6px] top-[6px] bottom-[6px] w-px bg-white/[0.05]" />
        {/* Fill line grows step by step from the top dot downward */}
        <motion.div
          className="absolute left-[6px] top-[6px] bottom-[6px] w-px origin-top"
          style={{ background: accent, opacity: 0.32 }}
          animate={{ scaleY: activeStep / (rows.length - 1) }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        />

        <div className="space-y-[21px] pl-[28px]">
          {rows.map((row, i) => {
            const completed = i <= activeStep;
            return (
              <motion.div
                key={row.label}
                className="relative flex items-center"
                animate={{ opacity: completed ? 1 : 0.2 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              >
                {/* Dot */}
                <div className="absolute -left-[28px] top-1/2 -translate-y-1/2">
                  <div
                    className="h-3 w-3 rounded-full transition-all duration-300"
                    style={{
                      background: completed ? accent : 'rgba(255,255,255,0.15)',
                      boxShadow: completed
                        ? row.final
                          ? `0 0 10px ${accent.replace('1)', '0.9)')}, 0 0 20px ${accent.replace('1)', '0.4)')}`
                          : `0 0 6px ${accent.replace('1)', '0.55)')}`
                        : 'none',
                    }}
                  />
                </div>

                {/* Label */}
                <span
                  className={`flex-1 leading-none ${
                    row.final && completed
                      ? 'text-[13px] font-semibold text-white'
                      : 'text-[13px] font-medium text-white/65'
                  }`}
                >
                  {row.label}
                </span>

                {/* Time + badge */}
                <div className="flex items-center gap-2 shrink-0 ml-3">
                  <span className="text-[10px] text-white/20 tabular-nums font-mono">
                    {row.time}
                  </span>
                  {row.tag === 'Auto' && (
                    <motion.span
                      className="text-[8px] font-bold uppercase tracking-[0.14em] rounded px-2 py-[3px]"
                      style={{
                        color: accent,
                        background: accent.replace('1)', '0.10)'),
                        border: `0.5px solid ${accent.replace('1)', '0.25)')}`,
                      }}
                      animate={{ opacity: completed ? 1 : 0, scale: completed ? 1 : 0.75 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    >
                      Auto
                    </motion.span>
                  )}
                  {row.tag === 'check' && (
                    <motion.div
                      className="flex h-[20px] w-[20px] items-center justify-center rounded-full"
                      style={{
                        background: accent.replace('1)', '0.18)'),
                        border: `0.5px solid ${accent.replace('1)', '0.38)')}`,
                      }}
                      animate={{ opacity: completed ? 1 : 0, scale: completed ? 1 : 0.65 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <Check className="h-3 w-3" style={{ color: accent }} />
                    </motion.div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Consulenza Digitale: automation convergence diagram ───────────────────────

function AutomationDiagram({ accent, step }: { accent: string; step?: number }) {
  const tasks = ['Email', 'Report', 'Follow-up', 'Preventivi', 'Inserimento dati', 'Approvazioni', 'Ordini'];
  const n = tasks.length;
  const activeStep = step ?? 0;
  const allDone = activeStep >= n;

  // Evenly spread y positions from 16 to 184 in 200px viewBox height
  const ys = tasks.map((_, i) => 16 + i * 28);

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between pb-2">
        <span className="text-[10px] uppercase tracking-[0.18em] text-white/25">
          Automation Engine
        </span>
        <div className="flex items-center gap-1.5">
          <motion.div
            className="h-2 w-2 rounded-full"
            style={{ background: accent }}
            animate={allDone ? { opacity: 1 } : { opacity: [1, 0.25, 1] }}
            transition={allDone ? { duration: 0.3 } : { duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.span
            key={allDone ? 'done' : 'running'}
            className="text-[10px] font-medium"
            style={{ color: accent }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            {allDone ? 'Completato' : 'In elaborazione'}
          </motion.span>
        </div>
      </div>

      {/* Diagram */}
      <svg
        width="100%"
        height="260"
        viewBox="0 0 320 200"
        style={{ display: 'block', overflow: 'visible' }}
      >
        {/* Task labels + connection beziers – one per task, each driven by activeStep */}
        {tasks.flatMap((task, i) => [
          <motion.g
            key={`label-${i}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: activeStep >= i ? 1 : 0 }}
            transition={{ duration: 0.38 }}
          >
            <text
              x={8}
              y={ys[i]}
              dominantBaseline="middle"
              textAnchor="start"
              fontSize={8.5}
              fill="rgba(255,255,255,0.52)"
              fontFamily="system-ui,-apple-system,BlinkMacSystemFont,sans-serif"
            >
              {task}
            </text>
          </motion.g>,

          <motion.path
            key={`path-${i}`}
            d={`M 102 ${ys[i]} C 132 ${ys[i]} 136 100 148 100`}
            fill="none"
            stroke={accent}
            strokeWidth={0.5}
            strokeOpacity={0.35}
            initial={{ pathLength: 0 }}
            animate={{ pathLength: activeStep >= i ? 1 : 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          />,
        ])}

        {/* Node: ambient glow rings (always visible, very faint) */}
        <circle cx={172} cy={100} r={44} fill="none" stroke={accent} strokeOpacity={0.03} strokeWidth={1.5} />
        <circle cx={172} cy={100} r={33} fill="none" stroke={accent} strokeOpacity={0.07} strokeWidth={0.5} />

        {/* Node: main body – brightens when all tasks connected */}
        <motion.circle
          cx={172} cy={100} r={24}
          fill="rgba(3,3,1,0.92)"
          stroke={accent}
          strokeWidth={0.5}
          animate={{ strokeOpacity: allDone ? 0.52 : 0.14 }}
          transition={{ duration: 0.6 }}
        />

        {/* Node: label text */}
        <text
          x={172} y={96}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={6}
          fill={accent}
          fillOpacity={0.8}
          letterSpacing="1.5"
          fontWeight={600}
          fontFamily="system-ui,-apple-system,BlinkMacSystemFont,sans-serif"
        >
          AUTOMATION
        </text>
        <text
          x={172} y={106}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={5.5}
          fill={accent}
          fillOpacity={0.42}
          letterSpacing="1"
          fontFamily="system-ui,-apple-system,BlinkMacSystemFont,sans-serif"
        >
          ENGINE
        </text>

        {/* Output arrow from node to FATTO */}
        <motion.path
          d="M 196 100 L 215 100"
          fill="none"
          stroke={accent}
          strokeWidth={0.5}
          strokeOpacity={0.45}
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: allDone ? 1 : 0, opacity: allDone ? 1 : 0 }}
          transition={{ duration: 0.4 }}
        />

        {/* ✓ FATTO – slides in from left when all tasks are connected */}
        <motion.g
          initial={{ x: -10, opacity: 0 }}
          animate={{ x: allDone ? 0 : -10, opacity: allDone ? 1 : 0 }}
          transition={{ delay: allDone ? 0.22 : 0, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <text
            x={220}
            y={100}
            textAnchor="start"
            dominantBaseline="middle"
            fontSize={17}
            fontWeight={700}
            fill={accent}
            fontFamily="system-ui,-apple-system,BlinkMacSystemFont,sans-serif"
          >
            ✓ FATTO
          </text>
        </motion.g>
      </svg>
    </div>
  );
}

// ── Single full-viewport card ─────────────────────────────────────────────────

function SolutionCard({
  sol,
  index,
  scrollStep,
}: {
  sol: (typeof solutions)[0];
  index: number;
  scrollStep?: number;
}) {
  const Icon = sol.icon;

  return (
    <div
      className="solution-card relative flex w-full flex-col overflow-x-hidden lg:min-h-[100dvh] lg:items-center lg:overflow-hidden"
      style={{ background: sol.bg }}
    >
      {/* Ambient glow */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: `radial-gradient(ellipse 60% 70% at ${index % 2 === 0 ? '70% 50%' : '30% 50%'}, ${sol.accentMid} 0%, transparent 65%)`,
        }}
      />

      {/* Giant ghost number */}
      <div
        data-card-number
        className="pointer-events-none absolute select-none font-black leading-none text-white/[0.022]"
        style={{
          fontSize: 'clamp(14rem, 32vw, 24rem)',
          top: '50%',
          right: index % 2 === 0 ? '-2%' : 'auto',
          left: index % 2 === 1 ? '-2%' : 'auto',
          transform: 'translateY(-50%)',
        }}
        aria-hidden
      >
        {sol.number}
      </div>

      {/* Content grid */}
      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-20 pt-24 lg:px-12 lg:py-24">
        <div className={`grid grid-cols-1 gap-16 lg:grid-cols-2 lg:gap-24 items-center ${index % 2 === 1 ? 'lg:[direction:rtl]' : ''}`}>

          {/* Left: text */}
          <div className={index % 2 === 1 ? 'lg:[direction:ltr]' : ''}>
            {/* Tag + title — sticky on mobile */}
            <div
              data-card-heading
              className="sticky top-[72px] z-10 -mx-6 px-6 pb-5 lg:static lg:mx-0 lg:px-0 lg:pb-0"
              style={{ backgroundColor: sol.bg }}
            >
              <div className="flex items-center gap-3 mb-8 pt-3 lg:pt-0">
                <div
                  className="flex h-11 w-11 items-center justify-center rounded-xl border"
                  style={{
                    background: sol.accent.replace('1)', '0.08)'),
                    borderColor: sol.accent.replace('1)', '0.25)'),
                  }}
                >
                  <Icon className="h-5 w-5" style={{ color: sol.accent }} />
                </div>
                <span
                  className="text-[11px] font-bold uppercase tracking-[0.2em]"
                  style={{ color: sol.accent.replace('1)', '0.55)') }}
                >
                  {sol.tag}
                </span>
                <span className="ml-auto font-mono text-[10px] text-white/15">{sol.number}</span>
              </div>

              <h2
                className="text-4xl font-black tracking-tight text-white lg:text-6xl leading-[1.05] whitespace-pre-line"
              >
                {sol.title}
              </h2>
            </div>

            <p data-card-body className="mt-6 text-base leading-7 text-gray-400 max-w-md">
              {sol.description}
            </p>

            <ul data-card-points className="mt-8 space-y-3.5">
              {sol.points.map((point, i) => (
                <motion.li
                  key={point}
                  className="flex items-start gap-3 text-sm text-gray-400"
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: '-10% 0px' }}
                  transition={{ delay: 0.2 + i * 0.1, duration: 0.5, ease: easing }}
                >
                  <span
                    className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{ background: sol.accent }}
                  />
                  {point}
                </motion.li>
              ))}
            </ul>

            <div data-card-cta>
              <motion.div
                className="mt-10"
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-10% 0px' }}
                transition={{ delay: 0.55, duration: 0.5, ease: easing }}
              >
                <Link
                  href="/contatti"
                  className="group inline-flex items-center gap-2.5 rounded-full border px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:brightness-125 active:scale-[0.97]"
                  style={{
                    borderColor: sol.accent.replace('1)', '0.35)'),
                    background: sol.accent.replace('1)', '0.09)'),
                  }}
                >
                  {sol.cta}
                  <ArrowRight
                    className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5"
                    style={{ color: sol.accent }}
                  />
                </Link>
              </motion.div>
            </div>
          </div>

          {/* Right: mockup card */}
          <div data-card-visual className={index % 2 === 1 ? 'lg:[direction:ltr]' : ''}>
          <motion.div
            className={`flex items-center justify-center`}
            initial={{ opacity: 0, scale: 0.88, y: 24 }}
            whileInView={{ opacity: 1, scale: 1, y: 0 }}
            viewport={{ once: true, margin: '-15% 0px' }}
            transition={{ duration: 0.9, delay: 0.1, ease: easing }}
          >
            <div
              className="absolute h-64 w-64 rounded-full blur-3xl pointer-events-none"
              style={{ background: sol.accent.replace('1)', '0.06)') }}
            />

            <motion.div
              className="relative w-full max-w-lg rounded-2xl border border-white/[0.08] bg-gray-900/70 p-6 backdrop-blur-sm"
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 5 + index * 0.5, repeat: Infinity, ease: 'easeInOut' }}
              whileHover={{ scale: 1.02 }}
            >
              {/* Card header */}
              <div className="flex items-center gap-2 mb-5 pb-4 border-b border-white/[0.06]">
                <div className="flex gap-1.5">
                  <div className="h-2 w-2 rounded-full bg-red-500/50" />
                  <div className="h-2 w-2 rounded-full bg-yellow-500/50" />
                  <div className="h-2 w-2 rounded-full bg-green-500/50" />
                </div>
                <span className="text-[10px] text-gray-600 ml-1">{sol.tag}</span>
                <motion.div
                  className="ml-auto h-1.5 w-1.5 rounded-full"
                  style={{ background: sol.accent }}
                  animate={{ opacity: [1, 0.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              </div>

              {index === 0 ? (
                <WebsiteMockup accent={sol.accent} />
              ) : index === 1 ? (
                <PlatformMockup accent={sol.accent} />
              ) : index === 2 ? (
                <QuoteProcessMockup accent={sol.accent} step={scrollStep ?? 0} />
              ) : index === 3 ? (
                <CRMTimelineMockup accent={sol.accent} step={scrollStep} />
              ) : index === 4 ? (
                <AutomationDiagram accent={sol.accent} step={scrollStep} />
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-2.5 mb-4">
                    {sol.stats.map((s, si) => (
                      <motion.div
                        key={s.l}
                        className="rounded-lg bg-white/[0.04] border border-white/[0.05] p-2.5"
                        initial={{ opacity: 0, y: 8 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 + si * 0.08, duration: 0.4, ease: easing }}
                      >
                        <div className="text-[9px] text-gray-600 mb-0.5">{s.l}</div>
                        <div
                          className="text-sm font-bold"
                          style={{ color: s.v === '0' ? sol.accent : 'white' }}
                        >
                          {s.v}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  <div className="rounded-lg bg-white/[0.03] border border-white/[0.04] p-3">
                    <div className="text-[9px] text-gray-600 mb-2">Andamento</div>
                    <BarChart bars={sol.bars} accent={sol.accent} />
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
          </div>
        </div>
      </div>

      {/* Bottom progress dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 items-center">
        {solutions.map((_, i) => (
          <div
            key={i}
            className="rounded-full transition-all duration-500"
            style={{
              width: i === index ? 20 : 6,
              height: 4,
              background: i === index ? sol.accent : 'rgba(255,255,255,0.12)',
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ── Section header ────────────────────────────────────────────────────────────

function SectionHeader() {
  return (
    <div className="bg-black pt-28 pb-16 border-t border-white/[0.05]">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-10% 0px' }}
          transition={{ duration: 0.7, ease: easing }}
        >
          <h2 className="text-4xl font-bold tracking-tight text-white lg:text-5xl max-w-xl text-balance">
            Le soluzioni che cambiano come lavori.
          </h2>
          <p className="mt-4 text-base leading-7 text-gray-500 max-w-lg">
            Dal sito web al CRM, dall&apos;interfaccia all&apos;automazione. Tutto progettato per fare la differenza.
          </p>
        </motion.div>
      </div>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function SolutionsStickyStack() {
  const containerRef = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const [card1Step, setCard1Step] = useState(0);
  const [card3Step, setCard3Step] = useState(0);
  const [card4Step, setCard4Step] = useState(0);

  useEffect(() => {
    if (reduce || !containerRef.current) return;

    const mm = gsap.matchMedia();

    mm.add('(min-width: 1024px)', () => {
      const panels = gsap.utils.toArray<HTMLElement>('.solution-card');
      if (panels.length < 5) return;

      const ctx = gsap.context(() => {

        // Staggered per-element parallax exit for any pinned panel.
        // Text column peels upward in layers; visual falls back; stripe flashes at midpoint.
        const exitCard = (panel: HTMLElement, nextPanel: HTMLElement) => {
          const heading = panel.querySelector('[data-card-heading]');
          const body    = panel.querySelector('[data-card-body]');
          const points  = panel.querySelector('[data-card-points]');
          const cta     = panel.querySelector('[data-card-cta]');
          const visual  = panel.querySelector('[data-card-visual]');
          const number  = panel.querySelector('[data-card-number]');
          const trg = (start: string, end: string) => ({
            trigger: nextPanel,
            start,
            end,
            scrub: true,
          });

          // Heading exits fastest and highest (foreground layer)
          if (heading) gsap.to(heading, { y: -64, opacity: 0, ease: 'power2.in', scrollTrigger: trg('top bottom', 'top 42%') });
          // Body copy follows slightly behind
          if (body)    gsap.to(body,    { y: -40, opacity: 0, ease: 'power2.in', scrollTrigger: trg('top 92%',   'top 32%') });
          // Bullet list a beat behind body
          if (points)  gsap.to(points,  { y: -24, opacity: 0, ease: 'power2.in', scrollTrigger: trg('top 88%',   'top 26%') });
          // CTA last in the text column
          if (cta)     gsap.to(cta,     { y: -14, opacity: 0, ease: 'power2.in', scrollTrigger: trg('top 84%',   'top 20%') });
          // Visual drops back and shrinks (opposite depth layer — creates spatial split)
          if (visual)  gsap.to(visual,  { y: 28, scale: 0.88, opacity: 0, ease: 'power2.in', scrollTrigger: trg('top 90%', 'top 14%') });
          // Ghost number blooms outward and dissolves
          if (number)  gsap.to(number,  { scale: 1.14, opacity: 0, ease: 'none', scrollTrigger: trg('top bottom', 'top top') });
          // Card shell scales back, stays dimly visible as backdrop
          gsap.to(panel, { scale: 0.88, opacity: 0.35, ease: 'none', scrollTrigger: trg('top bottom', 'top top') });
        };

        // Panels 0 and 1: pinned for the full stack duration
        [0, 1].forEach((pi) => {
          ScrollTrigger.create({
            trigger: panels[pi],
            start: 'top top',
            endTrigger: panels[panels.length - 1],
            end: 'top top',
            pin: true,
            pinSpacing: false,
          });
          exitCard(panels[pi], panels[pi + 1]);
        });

        // Panel 2 (Preventivatori AI): pinned with 4× extra scroll for quote animation
        let lastStep = -1;
        ScrollTrigger.create({
          trigger: panels[2],
          start: 'top top',
          end: () => `+=${window.innerHeight * 4}`,
          pin: true,
          pinSpacing: true,
          onUpdate: (self) => {
            const p = self.progress;
            const s = p < 0.2 ? 0 : p < 0.4 ? 1 : p < 0.6 ? 2 : 3;
            if (s !== lastStep) { lastStep = s; setCard1Step(s); }
          },
        });
        exitCard(panels[2], panels[3]);

        // Panel 3 (CRM): pinned with 5× extra scroll for timeline animation
        let lastStep3 = -1;
        ScrollTrigger.create({
          trigger: panels[3],
          start: 'top top',
          end: () => `+=${window.innerHeight * 5}`,
          pin: true,
          pinSpacing: true,
          onUpdate: (self) => {
            const p = self.progress;
            const s = p < 0.15 ? 0 : p < 0.30 ? 1 : p < 0.45 ? 2 : p < 0.60 ? 3 : p < 0.75 ? 4 : 5;
            if (s !== lastStep3) { lastStep3 = s; setCard3Step(s); }
          },
        });
        exitCard(panels[3], panels[4]);

        // Panel 4 (Consulenza Digitale): pinned with 6× extra scroll for automation diagram
        let lastStep4 = -1;
        ScrollTrigger.create({
          trigger: panels[4],
          start: 'top top',
          end: () => `+=${window.innerHeight * 6}`,
          pin: true,
          pinSpacing: true,
          onUpdate: (self) => {
            const p = self.progress;
            const s = p < 0.12 ? 0 : p < 0.24 ? 1 : p < 0.36 ? 2 : p < 0.48 ? 3 : p < 0.60 ? 4 : p < 0.72 ? 5 : p < 0.84 ? 6 : 7;
            if (s !== lastStep4) { lastStep4 = s; setCard4Step(s); }
          },
        });

      }, containerRef);

      return () => ctx.revert();
    });

    return () => mm.revert();
  }, [reduce]);

  return (
    <section id="soluzioni" className="w-full">
      <SectionHeader />

      <div ref={containerRef} className="relative w-full">
        {solutions.map((sol, i) => (
          <SolutionCard
            key={sol.number}
            sol={sol}
            index={i}
            scrollStep={i === 2 ? card1Step : i === 3 ? card3Step : i === 4 ? card4Step : undefined}
          />
        ))}
      </div>
    </section>
  );
}
